/**
 * Domain Configuration Manager
 *
 * Administrative interface for configuring domain primitive compositions,
 * circuit breaker settings, and safety parameters.
 *
 * Features:
 * - Visual domain primitive composition editor
 * - Circuit breaker configuration interface
 * - Safety threshold management
 * - Compliance framework validation
 * - Real-time configuration preview
 */
// @ts-nocheck


import { useMutation, useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import {
    GET_DOMAIN_CONFIGURATIONS,
    VALIDATE_DOMAIN_CONFIGURATION
} from '@ferp/graphql-client';

// Domain configuration mutations - fallback implementations if not properly exported
const CREATE_DOMAIN_CONFIGURATION = gql`
  mutation CreateDomainConfiguration($input: DomainConfigurationInput!) {
    createDomainConfiguration(input: $input) {
      success
      validation_errors
      domain
    }
  }
`;

const UPDATE_DOMAIN_CONFIGURATION = gql`
  mutation UpdateDomainConfiguration($input: DomainConfigurationInput!) {
    updateDomainConfiguration(input: $input) {
      success
      validation_errors
      domain
    }
  }
`;

const DELETE_DOMAIN_CONFIGURATION = gql`
  mutation DeleteDomainConfiguration($tenant: String!, $domain: String!) {
    deleteDomainConfiguration(tenant: $tenant, domain: $domain) {
      success
    }
  }
`;
import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Container,
    Grid,
    Group,
    Loader,
    Modal,
    MultiSelect,
    NumberInput,
    Paper,
    Stack,
    Switch,
    Tabs,
    Text,
    TextInput,
    Title
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    IconAlertTriangle,
    IconCheck,
    IconDatabase,
    IconEdit,
    IconEye,
    IconPlus,
    IconSettings,
    IconShield,
    IconTrash,
    IconX
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useState, useRef, useMemo, Suspense, Fragment, startTransition } from 'react';
import useMetadataRepository from '../hooks/useMetadataRepository';
import { useIntegratedSafety } from './IntegratedCircuitBreakerSafetyFramework';
import LifeCriticalSafetyProvider, { useSafety } from '../safety/LifeCriticalSafetyProvider';
import { useAuditTrail, ImmutableAuditTrail } from '../audit/ImmutableAuditTrail';
import { ErrorBoundary } from '../error/ErrorBoundary';

// Types
interface DomainConfiguration {
  domain: string;
  enabled_primitives: string[];
  circuit_breakers: {
    max_primitives_per_domain: number;
    emergency_fallback_enabled: boolean;
    load_threshold: number;
    performance_threshold_ms: number;
  };
  compliance_frameworks: string[];
  safety_score: number;
  metadata_version: string;
  created_at: string;
  updated_at: string;
  created_by: string;
}

interface DomainConfigurationInput {
  tenant: string;
  domain: string;
  enabled_primitives: string[];
  circuit_breakers: {
    max_primitives_per_domain: number;
    emergency_fallback_enabled: boolean;
    load_threshold: number;
    performance_threshold_ms: number;
  };
  compliance_frameworks: string[];
  custom_metadata?: Record<string, unknown>;
}

interface ValidationResult {
  is_valid: boolean;
  validation_errors: string[];
  safety_score_impact: number;
  compliance_warnings: string[];
  performance_impact: number;
}

// Constants
const ALL_PRIMITIVES = [
  'WorkflowDefinition',
  'ActorDescriptor',
  'TransitionCatalog',
  'Instance',
  'Signal',
  'ExecutionResult',
  'TimelineItem',
  'Task',
  'JobStatus',
  'Policy',
  'Context',
  'Schedule',
  'Resource',
  'Integration',
  'Escalation',
  'OptimizationRecommendation'
];

// Use centralized query export (camelCase fields)
import { GET_AVAILABLE_COMPLIANCE_FRAMEWORKS } from '@ferp/graphql-client';

interface ComplexityLevel {
  range: [number, number];
  color: string;
  description: string;
}

const COMPLEXITY_LEVELS: Record<string, ComplexityLevel> = {
  simple: { range: [3, 5], color: 'green', description: 'Basic workflows' },
  medium: { range: [6, 9], color: 'blue', description: 'Standard processes' },
  high: { range: [10, 12], color: 'orange', description: 'Complex operations' },
  extreme: { range: [13, 16], color: 'red', description: 'Life-critical systems' }
};

interface DomainConfigurationManagerProps {
  tenant: string;
}

export const DomainConfigurationManager: React.FC<DomainConfigurationManagerProps> = ({
  tenant
}) => {
  const [selectedDomain, setSelectedDomain] = useState<DomainConfiguration | null>(null);
  const [editingConfig, setEditingConfig] = useState<DomainConfigurationInput | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [availableFrameworks, setAvailableFrameworks] = useState<{frameworkId: string, name: string, description: string}[]>([]);
  const metadataRepoAny = useMetadataRepository() as any;
  const [complianceThresholds, setComplianceThresholds] = useState<{ excellent: number; good: number; warning: number; critical: number }>({ excellent: 0, good: 0, warning: 0, critical: 0 });

  // Safety framework integration
  const { safetyScore, validateSafety, auditAction } = useSafety();

  useEffect(() => {
    let mounted = true;
    const loadComplianceThresholds = async () => {
      try {
        const thresholds = await metadataRepoAny.getComplianceThresholds(tenant);
        if (mounted && thresholds) {
          setComplianceThresholds(thresholds);
        }
      } catch (error) {
        console.warn('Failed to load compliance thresholds:', error);
      }
    };

    loadComplianceThresholds();
    return () => {
      mounted = false;
    };
  }, [tenant, metadataRepoAny]);

  // Load domain configurations
  const {
    data: configsData,
    loading: configsLoading,
    error: configsError,
    refetch: refetchConfigs
  } = useQuery<{ getDomainConfigurations: DomainConfiguration[] }>(GET_DOMAIN_CONFIGURATIONS, {
    variables: { tenant },
    errorPolicy: 'all'
  });

  const domainConfigs = configsData?.getDomainConfigurations || [];

  // Load available compliance frameworks dynamically
  const {
    data: frameworksData,
    loading: frameworksLoading
  } = useQuery<Record<string, any>>(GET_AVAILABLE_COMPLIANCE_FRAMEWORKS, {
    variables: { tenant },
    onCompleted: (data: any) => {
      setAvailableFrameworks(data?.getAvailableComplianceFrameworks || []);
    }
  });

  // Mutations
  const [updateDomainConfig] = useMutation<Record<string, any>>(UPDATE_DOMAIN_CONFIGURATION);
  const [createDomainConfig] = useMutation<Record<string, any>>(CREATE_DOMAIN_CONFIGURATION);
  const [deleteDomainConfig] = useMutation<Record<string, any>>(DELETE_DOMAIN_CONFIGURATION);

  // Validation query
  const { refetch: validateConfig } = useQuery<Record<string, any>>(VALIDATE_DOMAIN_CONFIGURATION, {
    skip: true,
    onCompleted: (data: any) => {
      setValidationResult(data?.validateDomainConfiguration);
    }
  });

  // Initialize editing config when selecting domain
  useEffect(() => {
    if (selectedDomain && !editingConfig) {
      setEditingConfig({
        tenant,
        domain: selectedDomain.domain,
        enabled_primitives: selectedDomain.enabled_primitives,
        circuit_breakers: selectedDomain.circuit_breakers,
        compliance_frameworks: selectedDomain.compliance_frameworks
      });
    }
  }, [selectedDomain, editingConfig, tenant]);

  // Validate configuration when editing
  useEffect(() => {
    if (editingConfig) {
      const delayedValidation = setTimeout(() => {
        validateConfig({ variables: { input: editingConfig } }).catch((error: Error) => {
          console.warn('Configuration validation failed:', error);
        });
      }, 500);

      return () => clearTimeout(delayedValidation);
    }
    // No cleanup needed when editingConfig is null
    return undefined;
  }, [editingConfig, validateConfig]);

  // Get complexity level based on primitive count
  const getComplexityLevel = useCallback((primitiveCount: number): { level: string; range?: [number, number]; color: string; description: string } => {
    for (const [level, config] of Object.entries(COMPLEXITY_LEVELS)) {
      if (primitiveCount >= config.range[0] && primitiveCount <= config.range[1]) {
        return { level, ...config };
      }
    }
    return { level: 'unknown', color: 'gray', description: 'Invalid configuration' };
  }, []);

  // Save configuration
  const handleSaveConfiguration = useCallback(async () => {
    if (!editingConfig) return;

    try {
      // Validate safety before saving
      const safetyValidation = await validateSafety({
        action: 'DOMAIN_CONFIGURATION_CHANGE',
        domain: editingConfig.domain,
        tenant,
        data: editingConfig
      });

      if (safetyValidation.overall < 70) {
        notifications.show({
          title: 'Safety Validation Failed',
          message: 'Configuration changes do not meet safety requirements',
          color: 'red',
          icon: <IconX size={16} />
        });
        return;
      }

      const mutation = selectedDomain ? updateDomainConfig : createDomainConfig;
      const { data } = await mutation({
        variables: { input: editingConfig }
      });

      const result = data?.updateDomainConfiguration || data?.createDomainConfiguration;

      if (result.success) {
        // Audit configuration change
        await auditAction(
          'SAFETY_VALIDATION',
          {
            entityType: 'DomainConfiguration',
            entityId: editingConfig.domain,
            tenantId: tenant,
            domain: editingConfig.domain,
            primitiveCount: editingConfig.enabled_primitives.length,
            safetyScore: safetyValidation.overall,
            configurationAction: selectedDomain ? 'UPDATE_DOMAIN_CONFIGURATION' : 'CREATE_DOMAIN_CONFIGURATION'
          }
        );

        notifications.show({
          title: 'Configuration Saved',
          message: `Domain "${editingConfig.domain}" configuration saved successfully`,
          color: 'green',
          icon: <IconCheck size={16} />
        });

        refetchConfigs();
        setSelectedDomain(null);
        setEditingConfig(null);
        setShowCreateModal(false);
      } else {
        notifications.show({
          title: 'Validation Errors',
          message: result.validation_errors.join(', '),
          color: 'red',
          icon: <IconX size={16} />
        });
      }
    } catch (error) {
      console.error('Failed to save configuration:', error);
      notifications.show({
        title: 'Save Failed',
        message: 'Failed to save domain configuration',
        color: 'red',
        icon: <IconX size={16} />
      });
    }
  }, [editingConfig, selectedDomain, updateDomainConfig, createDomainConfig, refetchConfigs, tenant, validateSafety, auditAction]);

  // Delete configuration
  const handleDeleteConfiguration = useCallback(async (domain: string) => {
    try {
      // Audit delete action before performing it
      await auditAction(
        'SAFETY_VALIDATION',
        {
          entityType: 'DomainConfiguration',
          entityId: domain,
          tenantId: tenant,
          domain,
          deletedAt: new Date().toISOString(),
          configurationAction: 'DELETE_DOMAIN_CONFIGURATION'
        }
      );

      const { data } = await deleteDomainConfig({
        variables: { tenant, domain }
      });

      if (data?.deleteDomainConfiguration.success) {
        notifications.show({
          title: 'Configuration Deleted',
          message: `Domain "${domain}" configuration has been archived`,
          color: 'blue',
          icon: <IconCheck size={16} />
        });

        refetchConfigs();
        setShowDeleteModal(null);
        if (selectedDomain?.domain === domain) {
          setSelectedDomain(null);
          setEditingConfig(null);
        }
      }
    } catch (error) {
      console.error('Failed to delete configuration:', error);
      notifications.show({
        title: 'Delete Failed',
        message: 'Failed to delete domain configuration',
        color: 'red',
        icon: <IconX size={16} />
      });
    }
  }, [tenant, deleteDomainConfig, refetchConfigs, selectedDomain, auditAction]);

  // Render domain overview cards
  const renderDomainOverview = () => (
    <Grid>
      <Grid.Col span={12}>
        <Group justify="space-between" mb="md">
          <Title order={3}>Domain Configurations</Title>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setEditingConfig({
                tenant,
                domain: '',
                enabled_primitives: [],
                circuit_breakers: {
                  max_primitives_per_domain: 16,
                  emergency_fallback_enabled: true,
                  load_threshold: 80,
                  performance_threshold_ms: 200
                },
                compliance_frameworks: []
              });
              setShowCreateModal(true);
            }}
          >
            Create Domain
          </Button>
        </Group>
      </Grid.Col>

      {domainConfigs.map(config => {
        const complexity = getComplexityLevel(config.enabled_primitives.length);

        return (
          <Grid.Col key={config.domain} span={{ base: 12, md: 6, lg: 4 }}>
            <Card shadow="sm" padding="md" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text fw={600}>{config.domain}</Text>
                <Badge color={complexity.color} size="sm">
                  {complexity.level}
                </Badge>
              </Group>

              <Text size="sm" color="dimmed" mb="md">
                {config.enabled_primitives.length}/16 primitives enabled
              </Text>

              <Group gap="xs" mb="md">
                <Badge
                  color={config.safety_score >= complianceThresholds.good ? 'green' : 'red'}
                  leftSection={<IconShield size={12} />}
                >
                  Safety: {config.safety_score}
                </Badge>
                <Badge variant="light">
                  {config.compliance_frameworks.length} frameworks
                </Badge>
              </Group>

              <Group gap="xs">
                <ActionIcon
                  size="sm"
                  variant="light"
                  onClick={() => setSelectedDomain(config)}
                >
                  <IconEye size={16} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="light"
                  color="blue"
                  onClick={() => {
                    setSelectedDomain(config);
                    setActiveTab('configuration');
                  }}
                >
                  <IconEdit size={16} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="light"
                  color="red"
                  onClick={() => setShowDeleteModal(config.domain)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Card>
          </Grid.Col>
        );
      })}
    </Grid>
  );

  // Render configuration editor
  const renderConfigurationEditor = () => {
    if (!editingConfig) return null;

    const complexity = getComplexityLevel(editingConfig.enabled_primitives.length);

    return (
      <Stack gap="md">
        {/* Basic Information */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">Basic Information</Title>
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                label="Domain Name"
                required
                value={editingConfig.domain}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingConfig(prev => prev ? {
                  ...prev,
                  domain: e.target.value
                } : null)}
                disabled={!!selectedDomain}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Group gap="xs">
                <Text size="sm" fw={500}>Complexity Level:</Text>
                <Badge color={complexity.color}>
                  {complexity.level} ({editingConfig.enabled_primitives.length}/16)
                </Badge>
              </Group>
            </Grid.Col>
          </Grid>
        </Paper>

        {/* Primitive Selection */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">Primitive Composition</Title>
          <MultiSelect
            label="Enabled Primitives"
            description="Select the workflow primitives available for this domain"
            data={ALL_PRIMITIVES.map(primitive => ({
              value: primitive,
              label: primitive
            }))}
            value={editingConfig.enabled_primitives}
            onChange={(values: string[]) => setEditingConfig(prev => prev ? {
              ...prev,
              enabled_primitives: values
            } : null)}
            maxValues={16}
            searchable
            clearable
          />
          <Text size="xs" color="dimmed" mt="xs">
            {complexity.description}
          </Text>
        </Paper>

        {/* Circuit Breaker Configuration */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">Circuit Breaker Settings</Title>
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <NumberInput
                label="Max Primitives Per Domain"
                description="Maximum number of primitives that can be loaded simultaneously"
                value={editingConfig.circuit_breakers.max_primitives_per_domain}
                onChange={(value: string | number) => setEditingConfig(prev => prev ? {
                  ...prev,
                  circuit_breakers: {
                    ...prev.circuit_breakers,
                    max_primitives_per_domain: typeof value === 'number' ? value : 16
                  }
                } : null)}
                min={1}
                max={16}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <NumberInput
                label="Performance Threshold (ms)"
                description="Maximum acceptable load time before circuit breaker activation"
                value={editingConfig.circuit_breakers.performance_threshold_ms}
                onChange={(value: string | number) => setEditingConfig(prev => prev ? {
                  ...prev,
                  circuit_breakers: {
                    ...prev.circuit_breakers,
                    performance_threshold_ms: typeof value === 'number' ? value : 200
                  }
                } : null)}
                min={50}
                max={5000}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <NumberInput
                label="Load Threshold (%)"
                description="CPU/memory load threshold for load shedding activation"
                value={editingConfig.circuit_breakers.load_threshold}
                onChange={(value: string | number) => setEditingConfig(prev => prev ? {
                  ...prev,
                  circuit_breakers: {
                    ...prev.circuit_breakers,
                    load_threshold: typeof value === 'number' ? value : 80
                  }
                } : null)}
                min={10}
                max={100}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Switch
                label="Emergency Fallback Enabled"
                description="Automatically fallback to minimal primitive set during emergencies"
                checked={editingConfig.circuit_breakers.emergency_fallback_enabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingConfig(prev => prev ? {
                  ...prev,
                  circuit_breakers: {
                    ...prev.circuit_breakers,
                    emergency_fallback_enabled: e.target.checked
                  }
                } : null)}
              />
            </Grid.Col>
          </Grid>
        </Paper>

        {/* Compliance Frameworks */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">Compliance Frameworks</Title>
          <MultiSelect
            label="Required Compliance Frameworks"
            description="Select the regulatory frameworks this domain must comply with"
            data={availableFrameworks.map(framework => ({
              value: framework.frameworkId,
              label: `${framework.name} - ${framework.description}`
            }))}
            value={editingConfig.compliance_frameworks}
            onChange={(values: string[]) => setEditingConfig(prev => prev ? {
              ...prev,
              compliance_frameworks: values
            } : null)}
            searchable
            clearable
            disabled={frameworksLoading}
            placeholder={frameworksLoading ? "Loading frameworks..." : "Select compliance frameworks"}
          />
          {frameworksLoading && (
            <Group gap="xs" mt="xs">
              <Loader size="xs" />
              <Text size="xs" color="dimmed">Loading available compliance frameworks...</Text>
            </Group>
          )}
        </Paper>

        {/* Validation Results */}
        {validationResult && (
          <Paper p="md" withBorder>
            <Title order={4} mb="md">Configuration Validation</Title>
            <Stack gap="xs">
              <Group>
                <Text size="sm" fw={500}>Status:</Text>
                <Badge color={validationResult.is_valid ? 'green' : 'red'}>
                  {validationResult.is_valid ? 'Valid' : 'Invalid'}
                </Badge>
              </Group>

              {validationResult.validation_errors.length > 0 && (
                <Alert color="red" title="Validation Errors">
                  <Stack gap="xs">
                    {validationResult.validation_errors.map((error, index) => (
                      <Text key={index} size="sm">• {error}</Text>
                    ))}
                  </Stack>
                </Alert>
              )}

              {validationResult.compliance_warnings.length > 0 && (
                <Alert color="yellow" title="Compliance Warnings">
                  <Stack gap="xs">
                    {validationResult.compliance_warnings.map((warning, index) => (
                      <Text key={index} size="sm">• {warning}</Text>
                    ))}
                  </Stack>
                </Alert>
              )}

              <Group>
                <Text size="sm" fw={500}>Safety Score Impact:</Text>
                <Text size="sm" color={validationResult.safety_score_impact >= 0 ? 'green' : 'red'}>
                  {validationResult.safety_score_impact >= 0 ? '+' : ''}{validationResult.safety_score_impact}
                </Text>
              </Group>

              <Group>
                <Text size="sm" fw={500}>Performance Impact:</Text>
                <Text size="sm" color={validationResult.performance_impact <= 0 ? 'green' : 'red'}>
                  {validationResult.performance_impact >= 0 ? '+' : ''}{validationResult.performance_impact}ms
                </Text>
              </Group>
            </Stack>
          </Paper>
        )}

        {/* Actions */}
        <Group>
          <Button
            onClick={handleSaveConfiguration}
            disabled={validationResult != null && !validationResult.is_valid}
            leftSection={<IconCheck size={16} />}
          >
            Save Configuration
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setSelectedDomain(null);
              setEditingConfig(null);
              setShowCreateModal(false);
              setActiveTab('overview');
            }}
          >
            Cancel
          </Button>
        </Group>
      </Stack>
    );
  };

  // Loading state
  if (configsLoading) {
    return (
      <Container size="xl" py="xl">
        <Group gap="md" ta="center">
          <Loader size="sm" />
          <Text>Loading domain configurations...</Text>
        </Group>
      </Container>
    );
  }

  // Error state
  if (configsError) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Configuration Loading Error"
          color="red"
        >
          <Text size="sm">{configsError.message}</Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Title order={1} mb="xl">Domain Configuration Manager</Title>

      <Tabs value={activeTab} onChange={(value: string | null) => setActiveTab(value ?? 'overview')}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconDatabase size={16} />}>
            Overview
          </Tabs.Tab>
          {selectedDomain && (
            <Tabs.Tab value="configuration" leftSection={<IconSettings size={16} />}>
              Configuration
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          {renderDomainOverview()}
        </Tabs.Panel>

        {selectedDomain && (
          <Tabs.Panel value="configuration" pt="md">
            {renderConfigurationEditor()}
          </Tabs.Panel>
        )}
      </Tabs>

      {/* Create Modal */}
      <Modal
        opened={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingConfig(null);
        }}
        title="Create New Domain Configuration"
        size="xl"
      >
        {renderConfigurationEditor()}
      </Modal>

      {/* Delete Modal */}
      <Modal
        opened={!!showDeleteModal}
        onClose={() => setShowDeleteModal(null)}
        title="Delete Domain Configuration"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete the configuration for domain &quot;{showDeleteModal}&quot;?
            This action will archive the configuration and cannot be undone.
          </Text>
          <Group>
            <Button
              color="red"
              onClick={() => showDeleteModal && handleDeleteConfiguration(showDeleteModal)}
            >
              Delete Configuration
            </Button>
            <Button variant="outline" onClick={() => setShowDeleteModal(null)}>
              Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

// FERP-compliant export with full provider stack for life-critical safety
// Single execution path pattern: LifeCriticalSafetyProvider → ImmutableAuditTrail → ErrorBoundary → Component
export default function SafeDomainConfigurationManager(
  props: DomainConfigurationManagerProps
) {
  return (
    <LifeCriticalSafetyProvider>
      <ImmutableAuditTrail
        tenantId={props.tenant}
        userId="system"
        sessionId="domain-config-session"
        domain="system"
      >
        <ErrorBoundary>
          <DomainConfigurationManager {...props} />
        </ErrorBoundary>
      </ImmutableAuditTrail>
    </LifeCriticalSafetyProvider>
  );
}